/**
 * Flow Data Cleanup Utility
 *
 * Handles cleaning up flow-specific stepData when user changes their claim type
 * to prevent data conflicts between different flows (MEDICAL, CANCELLATION, BAGGAGE)
 */

import { MEDICAL_FLOW, CANCELLATION_FLOW, BAGGAGE_FLOW, COMMON_SCREENS } from '../config/flowDefinitions';
import type { FlowType } from '../config/flowDefinitions';

/**
 * Get all screen codes specific to a flow (excluding common screens)
 */
export const getFlowSpecificScreens = (flowType: FlowType): string[] => {
  let flow: string[];

  switch (flowType) {
    case 'MEDICAL':
      flow = MEDICAL_FLOW;
      break;
    case 'CANCELLATION':
      flow = CANCELLATION_FLOW;
      break;
    case 'BAGGAGE':
      flow = BAGGAGE_FLOW;
      break;
    default:
      return [];
  }

  // Return only screens that are NOT in COMMON_SCREENS
  return flow.filter(screenCode => !COMMON_SCREENS.includes(screenCode));
};

/**
 * Get all flow-specific screens from all flows (for cleanup purposes)
 */
export const getAllFlowSpecificScreens = (): string[] => {
  const medicalScreens = getFlowSpecificScreens('MEDICAL');
  const cancellationScreens = getFlowSpecificScreens('CANCELLATION');
  const baggageScreens = getFlowSpecificScreens('BAGGAGE');

  // Use Set to remove duplicates
  return Array.from(new Set([
    ...medicalScreens,
    ...cancellationScreens,
    ...baggageScreens
  ]));
};

/**
 * Clean stepData by removing flow-specific screens from other flows
 *
 * @param currentStepData - Current stepData from store
 * @param newFlowType - The new flow type selected by user
 * @returns Cleaned stepData with only common screens + new flow screens preserved
 */
export const cleanStepDataForFlowChange = (
  currentStepData: { [screenCode: string]: any },
  newFlowType: FlowType
): { [screenCode: string]: any } => {
  const newFlowScreens = getFlowSpecificScreens(newFlowType);
  const allFlowScreens = getAllFlowSpecificScreens();

  const cleanedStepData: { [screenCode: string]: any } = {};

  // Iterate through existing stepData
  Object.keys(currentStepData).forEach(screenCode => {
    // Keep the data if:
    // 1. It's a common screen (shared across all flows)
    // 2. OR it belongs to the new flow
    const isCommonScreen = COMMON_SCREENS.includes(screenCode);
    const isNewFlowScreen = newFlowScreens.includes(screenCode);
    const isFlowScreen = allFlowScreens.includes(screenCode);

    if (isCommonScreen || isNewFlowScreen) {
      // Keep this data
      cleanedStepData[screenCode] = currentStepData[screenCode];
    } else if (isFlowScreen) {
      // This is from another flow - remove it
    } else {
      // Unknown screen - keep it to be safe (might be a new screen)
      cleanedStepData[screenCode] = currentStepData[screenCode];
    }
  });

  return cleanedStepData;
};

/**
 * Get screens that will be removed when switching flows
 * Useful for showing a warning to the user
 *
 * @param currentStepData - Current stepData from store
 * @param newFlowType - The new flow type selected by user
 * @returns Array of screen codes that will be removed
 */
export const getScreensToBeRemoved = (
  currentStepData: { [screenCode: string]: any },
  newFlowType: FlowType
): string[] => {
  const newFlowScreens = getFlowSpecificScreens(newFlowType);
  const allFlowScreens = getAllFlowSpecificScreens();
  const removedScreens: string[] = [];

  Object.keys(currentStepData).forEach(screenCode => {
    const isCommonScreen = COMMON_SCREENS.includes(screenCode);
    const isNewFlowScreen = newFlowScreens.includes(screenCode);
    const isFlowScreen = allFlowScreens.includes(screenCode);

    // Will be removed if it's a flow screen but not in the new flow
    if (isFlowScreen && !isCommonScreen && !isNewFlowScreen) {
      removedScreens.push(screenCode);
    }
  });

  return removedScreens;
};

/**
 * Detect if user is attempting to change flow
 *
 * @param currentStepData - Current stepData from store
 * @param newFlowType - The new flow type being selected
 * @returns True if changing flow would result in data loss
 */
export const isFlowChangeDetected = (
  currentStepData: { [screenCode: string]: any },
  newFlowType: FlowType
): boolean => {
  const screensToRemove = getScreensToBeRemoved(currentStepData, newFlowType);
  return screensToRemove.length > 0;
};

/**
 * Get the current flow type based on stepData
 * Returns the flow that has the most screen data filled in
 */
export const detectCurrentFlow = (
  stepData: { [screenCode: string]: any }
): FlowType | null => {
  const medicalScreens = getFlowSpecificScreens('MEDICAL');
  const cancellationScreens = getFlowSpecificScreens('CANCELLATION');
  const baggageScreens = getFlowSpecificScreens('BAGGAGE');

  const medicalCount = Object.keys(stepData).filter(sc => medicalScreens.includes(sc)).length;
  const cancellationCount = Object.keys(stepData).filter(sc => cancellationScreens.includes(sc)).length;
  const baggageCount = Object.keys(stepData).filter(sc => baggageScreens.includes(sc)).length;

  // Return the flow with the most data
  if (medicalCount > 0 || cancellationCount > 0 || baggageCount > 0) {
    if (medicalCount >= cancellationCount && medicalCount >= baggageCount) {
      return 'MEDICAL';
    } else if (cancellationCount >= baggageCount) {
      return 'CANCELLATION';
    } else {
      return 'BAGGAGE';
    }
  }

  return null;
};
